// Custom hook for managing NAV history with Firestore
import { useState, useEffect } from 'react';
import { NAVHistory } from '../types';
import {
  getSeriesNAVHistory,
  getPoolNAVHistory,
  getLatestSeriesNAV,
  getLatestPoolNAV,
  subscribeSeriesNAVHistory,
  subscribePoolNAVHistory,
  calculateNAVChange,
  getSeriesPerformanceMetrics,
} from '../services/navService';

export const useSeriesNAVHistory = (
  seriesId: string,
  days?: number,
  realTime: boolean = false
) => {
  const [history, setHistory] = useState<NAVHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (realTime) {
      // Subscribe to real-time updates
      const unsubscribe = subscribeSeriesNAVHistory(
        seriesId,
        (updatedHistory) => {
          setHistory(updatedHistory);
          setLoading(false);
        },
        days,
        (err) => {
          setError(err.message);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } else {
      // Fetch once
      fetchHistory();
    }
  }, [seriesId, days, realTime]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const data = await getSeriesNAVHistory(seriesId, days);
      setHistory(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    history,
    loading,
    error,
    refresh: fetchHistory,
  };
};

export const usePoolNAVHistory = (days?: number, realTime: boolean = false) => {
  const [history, setHistory] = useState<NAVHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (realTime) {
      // Subscribe to real-time updates
      const unsubscribe = subscribePoolNAVHistory(
        (updatedHistory) => {
          setHistory(updatedHistory);
          setLoading(false);
        },
        days,
        (err) => {
          setError(err.message);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } else {
      // Fetch once
      fetchHistory();
    }
  }, [days, realTime]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const data = await getPoolNAVHistory(days);
      setHistory(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    history,
    loading,
    error,
    refresh: fetchHistory,
  };
};

export const useLatestSeriesNAV = (seriesId: string) => {
  const [latestNAV, setLatestNAV] = useState<NAVHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLatestNAV();
  }, [seriesId]);

  const fetchLatestNAV = async () => {
    try {
      setLoading(true);
      const data = await getLatestSeriesNAV(seriesId);
      setLatestNAV(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    latestNAV,
    loading,
    error,
    refresh: fetchLatestNAV,
  };
};

export const useLatestPoolNAV = () => {
  const [latestNAV, setLatestNAV] = useState<NAVHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLatestNAV();
  }, []);

  const fetchLatestNAV = async () => {
    try {
      setLoading(true);
      const data = await getLatestPoolNAV();
      setLatestNAV(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    latestNAV,
    loading,
    error,
    refresh: fetchLatestNAV,
  };
};

export const useNAVChange = (seriesId: string, days: number = 30) => {
  const [change, setChange] = useState<{ value: number; percentage: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchNAVChange();
  }, [seriesId, days]);

  const fetchNAVChange = async () => {
    try {
      setLoading(true);
      const data = await calculateNAVChange(seriesId, days);
      setChange(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    change,
    loading,
    error,
    refresh: fetchNAVChange,
  };
};

export const usePerformanceMetrics = (seriesId: string) => {
  const [metrics, setMetrics] = useState<{
    currentNAV: number;
    initialNAV: number;
    totalReturn: number;
    maxDrawdown: number;
    dataPoints: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMetrics();
  }, [seriesId]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const data = await getSeriesPerformanceMetrics(seriesId);
      setMetrics(data);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    metrics,
    loading,
    error,
    refresh: fetchMetrics,
  };
};
